import React, { useEffect, useState, useCallback, useRef } from "react";

import { DataLists } from "../../dataLists/DataLists";
import { dataListsType } from "../../shared/type";
import { Link, useNavigate, useParams } from "react-router-dom";
import debounce from "lodash/debounce";
import { BackIcon, SearchIcon } from "../../shared/icons";

// import AFSSearchPageAd from "../../components/AFSSearchPageAd";

interface GoogleSearchResult {
  title: string;
  link: string;
  snippet: string;
  pagemap?: {
    cse_thumbnail?: Array<{
      src: string;
    }>;
  };
}

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const { keyword } = useParams<{ keyword?: string }>();

  const [searchValue, setSearchValue] = useState("");
  const [isShowSearchResults, setIsShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState<dataListsType[]>([]);
  const [popularRecommendations, setPopularRecommendations] = useState<
    dataListsType[]
  >([]);
  const [googleResults, setGoogleResults] = useState<GoogleSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);

  // 用於追蹤當前的搜索關鍵詞，避免舊的搜索結果覆蓋新的狀態
  const currentSearchRef = useRef<string>("");
  // 用於取消 debounced 函數
  const debouncedSearchRef = useRef<ReturnType<typeof debounce> | null>(null);

  const getPopularRecommendations = () => {
    const allData = [...DataLists];
    const randomData = allData.sort(() => 0.5 - Math.random());
    setPopularRecommendations(randomData.slice(0, 12));
  };
  useEffect(() => {
    getPopularRecommendations();
  }, []);

  const performSearch = useCallback(async (inputValue: string) => {
    const trimmedValue = inputValue.trim();

    // 更新當前搜索關鍵詞的引用
    currentSearchRef.current = trimmedValue;

    if (trimmedValue === "") {
      setIsShowSearchResults(false);
      setSearchResults([]);
      setGoogleResults([]);
      setIsLoading(false);
      setIsLoadingGoogle(false);
      return;
    }

    setIsShowSearchResults(true);
    setIsLoading(true);

    // 篩選本地搜索結果
    const filteredResults = DataLists.filter(
      (item) =>
        item.category.toLowerCase().includes(trimmedValue.toLowerCase()) ||
        item.tags.some((tag) =>
          tag.toLowerCase().includes(trimmedValue.toLowerCase()),
        ) ||
        item.nation.toLowerCase().includes(trimmedValue.toLowerCase()),
    );

    // 檢查搜索關鍵詞是否仍然匹配（避免舊的搜索結果覆蓋新的狀態）
    if (currentSearchRef.current !== trimmedValue) {
      return;
    }

    setSearchResults(filteredResults);

    // 如果本地搜索有結果，立即結束loading
    if (filteredResults.length > 0) {
      setIsLoading(false);
    }

    // 開始Google搜尋請求，設置Google loading狀態
    setIsLoadingGoogle(true);

    // 發送Google搜尋請求（在背景執行，不影響loading狀態）
    try {
      const response = await fetch(
        `https://h5-backend-java-750219299255.asia-northeast1.run.app/search/googleSearch?query=${encodeURIComponent(
          trimmedValue,
        )}`,
      );
      const responseData = await response.json();
      // console.log("Backend API response:", responseData);

      // 再次檢查搜索關鍵詞是否仍然匹配（避免異步請求完成後覆蓋新的狀態）
      if (currentSearchRef.current !== trimmedValue) {
        return;
      }

      if (responseData.code === 400) {
        console.log("Search results exceed limit");
        setGoogleResults([]);
      } else if (
        responseData.code === 200 &&
        Array.isArray(responseData.data)
      ) {
        setGoogleResults(responseData.data);
        // console.log("Google search results:", responseData.data);
      } else {
        console.log("No search results found or invalid response format");
        setGoogleResults([]);
      }
    } catch (error) {
      console.error("Error fetching Google search results:", error);
      // 檢查搜索關鍵詞是否仍然匹配
      if (currentSearchRef.current === trimmedValue) {
        setGoogleResults([]);
      }
    } finally {
      // 再次檢查搜索關鍵詞是否仍然匹配
      if (currentSearchRef.current !== trimmedValue) {
        return;
      }

      // 結束Google loading狀態
      setIsLoadingGoogle(false);
      // 只有在本地搜索沒有結果時，才在Google搜索完成後結束loading
      if (filteredResults.length === 0) {
        setIsLoading(false);
      }
    }
  }, []);

  // 創建 debounced 搜索函數並保存引用
  useEffect(() => {
    debouncedSearchRef.current = debounce((value: string) => {
      performSearch(value);
    }, 1000);

    // 清理函數：組件卸載時取消 debounced 函數
    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [performSearch]);

  // 當 URL 中的 /search/:keyword 變化時，同步到輸入框並觸發搜尋
  useEffect(() => {
    if (!keyword) {
      // 沒有 keyword 時，取消之前的 debounced 搜索
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
      // 重置當前搜索關鍵詞
      currentSearchRef.current = "";
      // 重置搜尋狀態
      setSearchValue("");
      setIsShowSearchResults(false);
      setSearchResults([]);
      setGoogleResults([]);
      setIsLoading(false);
      setIsLoadingGoogle(false);
      return;
    }

    const decodedKeyword = decodeURIComponent(keyword);
    setSearchValue(decodedKeyword);

    // 取消之前的 debounced 搜索（如果有）
    if (debouncedSearchRef.current) {
      debouncedSearchRef.current.cancel();
    }

    // 觸發新的搜索
    if (debouncedSearchRef.current) {
      debouncedSearchRef.current(decodedKeyword);
    }
  }, [keyword]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchValue(inputValue);

    const trimmed = inputValue.trim();

    if (trimmed) {
      navigate(`/search/${encodeURIComponent(trimmed.toLowerCase())}`);
    } else {
      // 清空時，立即取消之前的 debounced 搜索
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
      // 立即重置當前搜索關鍵詞和狀態
      currentSearchRef.current = "";
      setIsShowSearchResults(false);
      setSearchResults([]);
      setGoogleResults([]);
      setIsLoading(false);
      setIsLoadingGoogle(false);
      navigate("/search");
    }
  };

  return (
    <>
      <div className="w-full h-full fixed top-0 left-0 -z-10 bg-white/90 bg-blend-overlay backdrop-blur-md"></div>
      <div className="w-full flex flex-col justify-center items-center px-8 py-10 bg-white/0">
        <div className="w-full h-[90px] max-w-[1116px] flex items-center justify-between font-bold">
          <span
            onClick={() => (window.location.href = "/")}
            className="mr-3 flex-shrink-0 cursor-pointer"
          >
            <BackIcon />
          </span>

          <div
            className="text-[34px] md:text-[42px] font-bold text-[#181818] cursor-pointer"
            onClick={() => (window.location.href = "/")}
          >
            clickandchill
          </div>
        </div>
        <div className="w-full max-w-[1116px]">
          <div className="flex flex-col items-center w-full">
            <span className="text-[#bcbcbc]/40 text-5xl md:text-6xl font-medium pt-6">
              SEARCH
            </span>
            <div className="flex justify-between items-center w-full mb-8 border-b-2 border-[#9ba2ac]">
              <input
                type="text"
                placeholder="Search destinations,activities and eat"
                className="w-full p-3 font-medium outline-none placeholder:text-[#bcbcbc] bg-transparent text-lg md:text-xl"
                value={searchValue}
                onChange={handleSearchChange}
              />
              <div className="w-[18px] h-[18px] md:w-7 md:h-7 mr-3 flex-shrink-0 opacity-50">
                <SearchIcon />
              </div>
            </div>
          </div>
          {isShowSearchResults ? (
            // 顯示搜索結果
            <div className="w-full flex flex-col mb-8">
              <span className="text-[#5c687a] text-[28px] font-semibold">
                Search Results for:
              </span>

              {/* <AFSSearchPageAd
                key={searchValue.trim().toLowerCase()} // ✅ query 變就強制重建
                enabled={isShowSearchResults && !isLoading}
                query={searchValue}
                pageName="search-page"
              /> */}

              {isLoading ? (
                // 顯示 loading 狀態
                <div className="flex justify-center items-center mt-8 py-8">
                  <div className="text-[#5c687a] text-lg md:text-2xl font-semibold">
                    Loading...
                  </div>
                </div>
              ) : searchResults.length > 0 || googleResults.length > 0 ? (
                <>
                  {/*  // 顯示本地搜索結果 */}
                  <ul className="flex flex-wrap gap-5 mt-3 w-full">
                    {searchResults.map((item) => {
                      const params = new URLSearchParams({
                        id: String(item.id),
                        category: item.category,
                        slug: item.urlinform,
                      });
                      return (
                        <div
                          key={item.id}
                          className="w-full border-b pb-4 md:pb-8 max-w-[95%]"
                        >
                          <Link
                            rel="stylesheet"
                            target="_top"
                            // to={{
                            //   pathname: `/article`,
                            //   // search: `?id=${item.id}&${item.category}/${item.urlinform}/`,
                            //   search: params.toString(),
                            // }}
                            to={`/article/${item.urlinform}`}
                            // key={item.id}
                            className="text-[#1a0dab] text-[22px] font-semibold cursor-pointer"
                          >
                            {item.title}
                          </Link>
                          <div className="flex gap-2 w-full justify-between">
                            <p className="text-[#4d5156] text-sm md:w-[calc(100%-104px-156px)]">
                              {item.description}
                            </p>
                            <li className="w-[104px] h-[104px] rounded-lg overflow-hidden shadow-md border-[1px] flex-shrink-0">
                              <img
                                src={item.images[0]}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            </li>
                          </div>
                        </div>
                      );
                    })}
                  </ul>
                  {/* 如果Google搜索還在進行中，顯示loading狀態 */}
                  {isLoadingGoogle && searchResults.length > 0 && (
                    <div className="flex justify-center items-center mt-5 py-4">
                      <div className="text-[#5c687a] text-base md:text-lg font-medium">
                        Loading more results...
                      </div>
                    </div>
                  )}
                  {/* 顯示Google搜索結果 {googleResults.length > 0 && ( */}
                  <div className="mt-5">
                    {/* <span className="text-[#5c687a] text-xl font-semibold">
                      Google Search Results:
                    </span> */}
                    <div className="mt-0 space-y-4">
                      {googleResults.map((result, index) => (
                        <div
                          key={index}
                          className="w-full border-b pb-4 md:pb-8 max-w-[95%]"
                        >
                          <a
                            href={result.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#1a0dab] text-[22px] font-semibold"
                          >
                            {result.title}
                          </a>
                          <div className="flex gap-2 w-full justify-between">
                            <p className="text-[#4d5156] text-sm md:w-[calc(100%-104px-156px)]">
                              {result.snippet}
                            </p>
                            {result.pagemap?.cse_thumbnail && (
                              <li className="w-[104px] h-[104px] rounded-lg overflow-hidden shadow-md border-[1px] flex-shrink-0">
                                <img
                                  src={result.pagemap.cse_thumbnail[0].src}
                                  alt={result.title}
                                  // className="w-32 h-32 mt-2 rounded-full object-cover shadow-md border-[1px]"
                                  className="w-full h-full object-cover"
                                />
                              </li>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* )} */}
                </>
              ) : (
                // 顯示 "No results found"
                <p className="text-[#9ba2ac] text-base md:text-2xl mt-3">
                  No results found...
                </p>
              )}
            </div>
          ) : (
            // 顯示推薦結果
            <div className="flex flex-col">
              <div className="flex flex-col mb-6">
                {/* <p className="text-[#5c687a] text-[28px] font-semibold">
                  Suggested keywords:
                </p> */}
                <div className="gap-2 flex flex-wrap md:gap-4">
                  {[
                    "Travel",
                    "Guides",
                    "Food",
                    "Hotels",
                    "China",
                    "Japan",
                    "Indonesia",
                  ].map((item) => (
                    <span
                      key={item}
                      className="py-1.5 px-3 bg-[#c9b077]/70 text-white hover:bg-[#c9b077] rounded-full text-sm md:text-base font-medium cursor-pointer"
                      onClick={() => {
                        setSearchValue(item);
                        setIsShowSearchResults(true);
                        setIsLoading(true);
                        handleSearchChange({
                          target: { value: item },
                        } as React.ChangeEvent<HTMLInputElement>);
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <span className="text-[#5c687a] text-[28px] font-semibold">
                Recommendations:
              </span>
              <ul className="flex flex-wrap gap-5 mt-3 mb-8">
                {popularRecommendations.map((item) => {
                  const params = new URLSearchParams({
                    id: String(item.id),
                    category: item.category,
                    slug: item.urlinform,
                  });
                  return (
                    <div
                      key={item.id}
                      className="w-full border-b pb-4 md:pb-8 max-w-[95%]"
                    >
                      <Link
                        rel="stylesheet"
                        target="_top"
                        // to={{
                        //   pathname: `/article`,
                        //   // search: `?id=${item.id}&${item.category}/${item.urlinform}/`,
                        //   search: params.toString(),
                        // }}
                        to={`/article/${item.urlinform}`}
                        // key={item.id}
                        className="text-[#1a0dab] text-[22px] font-semibold cursor-pointer"
                      >
                        {item.title}
                      </Link>
                      <div className="flex gap-2 w-full justify-between">
                        <p className="text-[#4d5156] text-sm md:w-[calc(100%-104px-156px)]">
                          {item.description}
                        </p>
                        <li className="w-[104px] h-[104px] rounded-lg overflow-hidden shadow-md border-[1px] flex-shrink-0">
                          <img
                            src={item.images[0]}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        </li>
                      </div>
                    </div>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchPage;
